import React, { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAppSelector } from "../store/hooks";
import { Role } from "@/constants/enums/user/role";
import { hasAdminPortalAccess, isVerifierUser } from "@/utils/accessControl";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: Role;
  allowNonAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = null,
  allowNonAdmin = false,
}) => {
  const { isAuthenticated, loading, user } = useAppSelector(
    (state) => state.auth,
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin portal: Super Admin (all modules) + limited Admin
  if (requiredRole === Role.ADMIN || requiredRole === Role.SUPER_ADMIN) {
    if (!hasAdminPortalAccess(user)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Verifier-only routes
  if (allowNonAdmin) {
    if (!isVerifierUser(user)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // General role requirement check (for any other specific roles in the future)
  if (
    requiredRole &&
    requiredRole !== Role.ADMIN &&
    requiredRole !== Role.SUPER_ADMIN &&
    user?.role !== requiredRole
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
